import { useCallback, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { Popover } from '@opendoor/atoms/popover';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import { PopoverRadioOptions } from 'components/marketplace/shared/PopoverRadioOptions';
import {
  MARKETPLACE_POPOVER_SURFACE_CLASS,
  type PopoverTriggerRender,
} from 'components/marketplace/shared/popoverStyles';

const AVAILABILITY_OPTIONS = [
  { value: 'forSale', label: 'For sale' },
  { value: 'availableSoon', label: 'Available soon' },
] as const;

type AvailabilityValue = (typeof AVAILABILITY_OPTIONS)[number]['value'];

interface QuickAvailabilityFilterProps {
  availableSoon: boolean;
  onChange: (availableSoon: boolean) => void;
}

export const QuickAvailabilityFilter = ({
  availableSoon,
  onChange,
}: QuickAvailabilityFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = availableSoon ? AVAILABILITY_OPTIONS[1] : AVAILABILITY_OPTIONS[0];

  const renderTrigger = useCallback<PopoverTriggerRender>(
    (props) => (
      <Button
        {...props}
        analyticsName="marketplace-inventory-filter"
        aria-label={`Listing availability: ${selectedOption.label}`}
        size="sm"
        variant="secondary"
        className="h-10 shrink-0 px-3"
      >
        {selectedOption.label}
        <IconChevronDown size={16} aria-hidden />
      </Button>
    ),
    [selectedOption.label],
  );

  const handleValueChange = useCallback(
    (value: AvailabilityValue) => {
      onChange(value === 'availableSoon');
      setIsOpen(false);
    },
    [onChange],
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger render={renderTrigger} />
      <Popover align="start" size="sm" className={cn('w-52', MARKETPLACE_POPOVER_SURFACE_CLASS)}>
        <Popover.Title className="sr-only">Listing availability</Popover.Title>
        <PopoverRadioOptions
          aria-label="Listing availability"
          analyticsName="marketplace-inventory-filter"
          options={AVAILABILITY_OPTIONS}
          value={selectedOption.value}
          onValueChange={handleValueChange}
        />
      </Popover>
    </Popover.Root>
  );
};
